import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BranchViewPresentational } from "./branchView";
import { LoadingIndicator } from "../../shared/alerts/loadingIndicator";
import { ErrorDisplay } from "../../shared/alerts/errorDisplay";
import { useGetBranchByIdQuery, useDeleteBranchMutation } from "../../../api/branchApi";
import { useGetCompanyByIdQuery } from "../../../api/companyApi";
import { QrCodePopup } from "../../shared/qrCode/qrPopup";
import { ConfirmationDialog } from "../../shared/confirmationDialog/confirmationDialog";
import { useToast } from "../../../hooks/useToast";
import { useLoader } from "../../../hooks/useLoader";
import { Collection } from "../../../enums/collections.enum";

export const BranchViewContainer: React.FC = () => {
    const { branchId, companyId: companyIdParam } = useParams<{ branchId?: string; companyId?: string }>();
    const navigate = useNavigate();
    const bId = branchId ?? "";
    const { data: branch, isLoading, error } = useGetBranchByIdQuery(bId);
    const { data: company } = useGetCompanyByIdQuery(companyIdParam ?? '');
    const [qrPopupOpen, setQrPopupOpen] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteBranch] = useDeleteBranchMutation();
    const { showSuccess, showError } = useToast();
    const { showLoader, hideLoader } = useLoader();

    // Handle QR code click
    const handleQrCodeClick = () => {
        setQrPopupOpen(true);
    };

    // Close QR popup
    const handleQrPopupClose = () => {
        setQrPopupOpen(false);
    };

    // Handle delete branch click
    const handleDeleteClick = () => {
        setConfirmDialogOpen(true);
    };

    // Handle confirm delete
    const handleConfirmDelete = async () => {
        if (!branch) return;

        try {
            showLoader();
            await deleteBranch(branch).unwrap();
            showSuccess("Branch deleted successfully.");
            navigate(`/${Collection.Companies}/${branch.companyId}`);
        } catch (error: any) {
            if (error?.status === 400) {
                showError(error?.message || "Cannot delete branch.");
            } else {
                showError("Error deleting branch.");
            }
        } finally {
            hideLoader();
        }
        setConfirmDialogOpen(false);
    };

    // Handle cancel delete
    const handleCancelDelete = () => {
        setConfirmDialogOpen(false);
    };

    // Handle loading and error states
    if (isLoading) return <LoadingIndicator message="Loading branch data..." />;
    if (error) return <ErrorDisplay message="Error fetching branch data." />;
    if (!branch) {
        return <p>Branch not found</p>;
    }
    if (!company) {
        return <p>Company not found</p>;
    }

    return <>
        <BranchViewPresentational 
            branch={branch} 
            company={company} 
            onQrCodeClick={handleQrCodeClick}
            onDelete={handleDeleteClick}
        />
        <QrCodePopup
            open={qrPopupOpen}
            onClose={handleQrPopupClose}
            qrValue={window.location.href}
            companyName={company.name}
            shortUrl={branch.urlKey ? `${window.location.origin}/${branch.urlKey}` : undefined}
        />
        <ConfirmationDialog
            open={confirmDialogOpen}
            title="Delete Branch"
            message={`Are you sure you want to delete the branch "${branch.name}"? This action cannot be undone.`}
            confirmButtonText="Delete"
            cancelButtonText="Cancel"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
        />
    </>;
};

